const formatTime = (seconds) => {
  if (!seconds || seconds < 1) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const Review = ({ questions, setCurrentQuestion, timeSpent = {} }) => {
  const answered = questions.filter(q => q.selectedOption != null).length;
  const bookmarked = questions.filter(q => q.bookMarked).length;
  const unanswered = questions.filter(q => q.selectedOption == null && !q.bookMarked).length;

  return (
    <div
      className="w-full min-h-full overflow-y-auto"
      style={{ background: 'linear-gradient(180deg, #0d0f1a 0%, #111320 100%)', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}
    >
      {/* Sticky header */}
      <div
        className="sticky top-0 z-10 border-b border-white/[0.07]"
        style={{ background: 'rgba(13,15,26,0.92)', backdropFilter: 'blur(16px)' }}
      >
        <div className="px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-white/90">Section Review</h2>
              <p className="text-xs text-white/35 mt-0.5">Review your answers before submitting.</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-0.5">Progress</p>
              <p className="text-lg font-bold tabular-nums text-white/80">
                {answered}
                <span className="text-white/25 text-sm font-normal"> / {questions.length}</span>
              </p>
            </div>
          </div>

          {/* Stats pills */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/[0.10] border border-blue-400/25">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
              <span className="text-xs font-semibold text-blue-300">{answered} Answered</span>
            </div>
            {bookmarked > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500/[0.10] border border-rose-400/25">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                <span className="text-xs font-semibold text-rose-300">{bookmarked} Marked for Review</span>
              </div>
            )}
            {unanswered > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
                <div className="w-1.5 h-1.5 rounded-full bg-white/30 flex-shrink-0" />
                <span className="text-xs font-semibold text-white/35">{unanswered} Unanswered</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 pb-28">
        {unanswered > 0 && (
          <div className="mb-5 flex items-center gap-2.5 rounded-xl px-4 py-3 border border-amber-400/20"
            style={{ background: 'rgba(245,158,11,0.07)' }}
          >
            <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-sm text-amber-300/80">
              You have <span className="font-bold text-amber-300">{unanswered}</span> unanswered question{unanswered !== 1 ? 's' : ''}. Click a bubble to go back.
            </p>
          </div>
        )}

        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-3">
          {questions.map((ques, idx) => {
            const isBookmarked = ques.bookMarked;
            const isAnswered = ques.selectedOption != null;

            return (
              <button
                key={idx}
                onClick={() => setCurrentQuestion(questions[idx])}
                className="group flex flex-col items-center gap-1 transition-transform duration-150 hover:scale-105"
              >
                {/* Glossy number tile */}
                <div
                  className={`relative w-12 h-12 rounded-xl flex items-center justify-center font-semibold text-sm border shadow-sm overflow-hidden transition-all duration-200
                    ${isBookmarked
                      ? 'border-rose-400/40 text-rose-200 shadow-rose-500/15'
                      : isAnswered
                        ? 'border-blue-400/30 text-blue-100 shadow-blue-500/15'
                        : 'border-white/[0.08] text-white/30'
                    }`}
                  style={
                    isBookmarked
                      ? { background: 'radial-gradient(ellipse at 50% 0%, rgba(244,63,94,0.28) 0%, rgba(255,255,255,0.04) 100%)' }
                      : isAnswered
                        ? { background: 'radial-gradient(ellipse at 50% 0%, rgba(96,165,250,0.22) 0%, rgba(255,255,255,0.04) 100%)' }
                        : { background: 'rgba(255,255,255,0.03)' }
                  }
                >
                  {/* Top gloss shine */}
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none" />
                  {idx + 1}
                </div>

                {/* Answer label */}
                <span className={`text-[10px] font-semibold tabular-nums
                  ${isBookmarked ? 'text-rose-400' : isAnswered ? 'text-white/50' : 'text-white/20'}`}
                >
                  {ques.selectedOption != null ? ques.selectedOption.toUpperCase() : '—'}
                </span>

                {/* Time spent */}
                <span className="text-[9px] tabular-nums text-white/25">
                  {formatTime(timeSpent[ques.questionIds]) ?? '—'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Review;
